import { RefreshButton } from "@shared/components/ui/Button";
import { setScrollContainer } from "@shared/utils/scroll";
import theme from "@shared/utils/themeColors";
import React, { ReactNode } from "react";
import { Toaster } from "react-hot-toast";
import DropdownFilter from "../components/DropdownFilter";
import EmptyState from "../components/EmptyState";
import { Header } from "../components/header/Header";
import SearchForm from "../components/SearchForm";
import Sidebar from "./Sidebar";

interface PageHeaderProps {
  title?: string;
  search?: {
    show?: boolean;
    placeholder?: string;
    onChange?: (value: string) => void;
  };
  filter?: {
    show?: boolean;
    options?: Array<{ label: string; value: string }>;
    onChange?: (value: string) => void;
  };
  refresh?: {
    show?: boolean;
    onRefresh?: () => void;
  };
  actions?: {
    label: string;
    onClick: () => void;
  }[];
}

interface ContentState {
  isLoading?: boolean;
  error?: Error | null;
  isEmpty?: boolean;
  emptyStateProps?: {
    title?: string;
    description?: string;
    icon?: ReactNode;
    action?: {
      label: string;
      onClick: () => void;
    };
  };
}

interface AuthenticatedLayoutProps extends ContentState {
  children: ReactNode;
  className?: string;
  defaultCollapsed?: boolean;
  sidebar?: ReactNode;
  pageHeader?: PageHeaderProps;
}

export default function AuthenticatedLayout({
  children,
  className = "",
  defaultCollapsed = true,
  isLoading = false,
  error = null,
  isEmpty = false,
  emptyStateProps = {},
  sidebar,
  pageHeader,
}: AuthenticatedLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = React.useState(!defaultCollapsed);

  const renderPageHeader = () => {
    if (!pageHeader) return null;

    return (
      <div className="px-4 sm:px-6 lg:px-8 pt-8 w-full max-w-9xl mx-auto sm:flex sm:justify-between sm:items-center">
        {/* Title */}
        {pageHeader.title && (
          <div className="mb-4 sm:mb-0">
            <h1
              className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}
            >
              {pageHeader.title}
            </h1>
          </div>
        )}

        {/* Actions */}
        {(pageHeader.search ||
          pageHeader.filter ||
          pageHeader.refresh ||
          pageHeader.actions) && (
          <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
            {pageHeader.search?.show && (
              <SearchForm
                placeholder={pageHeader.search.placeholder}
                onChange={pageHeader.search.onChange}
              />
            )}
            {pageHeader.filter?.show && (
              <DropdownFilter
                options={pageHeader.filter.options}
                onChange={pageHeader.filter.onChange}
              />
            )}
            {pageHeader.refresh?.show && (
              <RefreshButton
                onClick={pageHeader.refresh.onRefresh}
                isLoading={isLoading}
              />
            )}
            {pageHeader.actions?.map((action) => (
              <button
                key={action.label}
                onClick={action.onClick}
                className="inline-flex items-center px-4 py-2 border border-transparent btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <EmptyState
          title="Loading..."
          description="Please wait while we fetch the content"
          icon={
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-blue-200 to-blue-100 mb-4">
              <svg className="w-6 h-6 animate-spin" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          }
        />
      );
    }

    if (error) {
      return (
        <EmptyState
          title="Error loading content"
          description={
            error.message ||
            "There was an error loading the content. Please try again later."
          }
          icon={
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-200 to-red-100 mb-4">
              <svg
                className={`w-6 h-6 ${theme.text.error}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          }
        />
      );
    }

    if (isEmpty) {
      return (
        <EmptyState
          title={emptyStateProps.title || "No content found"}
          description={
            emptyStateProps.description || "There is no content to display"
          }
          icon={emptyStateProps.icon}
          action={emptyStateProps.action}
        />
      );
    }

    return children;
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div
        className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden"
        ref={(el) => el && setScrollContainer(el)}
      >
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        {renderPageHeader()}

        {sidebar ? (
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
              <div className="flex flex-col md:flex-row md:-mr-px">
                {sidebar}
                <main className="flex-grow">{renderContent()}</main>
              </div>
            </div>
          </div>
        ) : (
          <main className={`${className}`}>{renderContent()}</main>
        )}

        <Toaster position="top-center" />
      </div>
    </div>
  );
}
